import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import { MdArrowDropDown, MdCheck, MdLocalShipping } from "react-icons/md";
import { carrierDisplayName } from "../../Carriers/carrierHelpers";

// What "show first" can be: newest orders (as the list always was), every
// carrier's orders, or one carrier's.
export const NEWEST = "";
export const ALL_CARRIERS = "any";

const KEY = "orders:carrierFirst";

// The "show first" choice this device keeps for this login ("" when none or
// no longer offered).
export const readCarrierFirst = (userId, carriers) => {
  try {
    const saved = localStorage.getItem(`${KEY}:${userId ?? ""}`) ?? "";
    return saved === ALL_CARRIERS ||
      carriers.some((carrier) => carrier.value === saved)
      ? saved
      : NEWEST;
  } catch {
    return NEWEST;
  }
};

export const saveCarrierFirst = (userId, value) => {
  try {
    const key = `${KEY}:${userId ?? ""}`;
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Not remembered (storage off): it lasts until the page is left.
  }
};

// A carrier as users read it ("FedEx", "ICS Courier"), whatever the list
// spells ("Fedex", "Ics").
const carrierName = (value, carriers) => {
  const carrier = carriers.find((option) => option.value === value);
  return carrierDisplayName(carrier?.label ?? value);
};

export const firstLabel = (value, carriers) => {
  if (!value) return "";
  if (value === ALL_CARRIERS) return "Carriers first";
  return `${carrierName(value, carriers)} first`;
};

// What the list's "Show first" chip says ("UPS", "Every carrier"); "" when
// the list shows the newest orders first, as it always did.
export const firstChipValue = (value, carriers) => {
  if (!value) return "";
  if (value === ALL_CARRIERS) return "Every carrier";
  return carrierName(value, carriers);
};

const PANEL_WIDTH = 260;
const PHONE = 640;

const Choice = ({ checked, onClick, label, hint }) => (
  <button
    type="button"
    role="menuitemradio"
    aria-checked={checked}
    onClick={onClick}
    className={`flex min-h-[40px] w-full items-center gap-[8px] rounded-md px-[10px] py-[6px] text-left text-[13px] transition-colors lg:min-h-[32px] lg:text-[12.5px] [@media(pointer:coarse)]:min-h-[44px] ${
      checked
        ? "bg-brand-light font-semibold text-brand"
        : "text-ui-text hover:bg-ui-row-hover"
    }`}
  >
    <span className="flex h-4 w-4 shrink-0 items-center justify-center">
      {checked && <MdCheck aria-hidden="true" className="h-4 w-4" />}
    </span>
    <span className="grid min-w-0 gap-[1px]">
      <span className="truncate">{label}</span>
      {hint && (
        <span className="text-[11.5px] font-normal leading-[1.35] text-ui-text-muted">
          {hint}
        </span>
      )}
    </span>
  </button>
);

/**
 * The toolbar's Carriers control. The switch shows the orders still to fulfil
 * that leave with a carrier (GLS, Canpar, UPS…), as Self delivery does for
 * self-delivery orders; clicking it again goes back to all orders. The arrow
 * opens "Show first": the newest orders (as the list always was), every
 * carrier's orders, or one carrier's (GLS first, UPS first…), then the rest.
 * That choice applies whether the switch is on or not and stays on this
 * device for this login.
 *
 * `on` follows the list's filters; `onChange(on)`; `first` / `onFirstChange`
 * the "show first" value ("", "any" or a carrier's value from `carriers`).
 */
const CarrierOrdersMenu = ({
  on,
  onChange,
  first,
  onFirstChange,
  carriers,
}) => {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState(null);
  const groupRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  // Under the control, inside the screen; a sheet from the bottom on phones.
  const measure = useCallback(() => {
    const box = groupRef.current?.getBoundingClientRect();
    if (!box) return;
    if (window.innerWidth < PHONE) {
      setPlace({ phone: true });
      return;
    }
    const left = Math.max(
      8,
      Math.min(box.left, window.innerWidth - PANEL_WIDTH - 8),
    );
    setPlace({
      left,
      top: box.bottom + 6,
      maxHeight: Math.max(160, window.innerHeight - box.bottom - 16),
    });
  }, []);

  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      close();
    };
    const onDown = (event) => {
      if (
        panelRef.current?.contains(event.target) ||
        groupRef.current?.contains(event.target)
      )
        return;
      close();
    };
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open, close, measure]);

  const pick = (value) => {
    onFirstChange(value);
    close();
  };

  const shownFirst = firstLabel(first, carriers);
  const segment = (active) =>
    `flex items-center gap-[5px] whitespace-nowrap border px-[11px] py-[5px] text-xs font-semibold transition-colors ${
      active
        ? "border-brand bg-brand-light text-brand"
        : "border-ui-border bg-ui-surface text-ui-text-muted hover:border-brand hover:bg-brand-light hover:text-brand"
    }`;

  const panel = open && place && (
    <div
      ref={panelRef}
      role="menu"
      aria-label="Show first"
      style={
        place.phone
          ? undefined
          : {
              position: "fixed",
              left: place.left,
              top: place.top,
              width: PANEL_WIDTH,
              maxHeight: place.maxHeight,
            }
      }
      className={`z-[1400] overflow-y-auto rounded-lg border-[1px] border-ui-border bg-ui-surface p-[6px] shadow-ui-md thin__scrollbar ${
        place.phone
          ? "fixed inset-x-0 bottom-0 max-h-[75vh] rounded-b-none pb-[max(8px,env(safe-area-inset-bottom))]"
          : ""
      }`}
    >
      <div className="px-[10px] pb-[4px] pt-[6px] text-[10px] font-semibold uppercase tracking-wide text-ui-text-subtle">
        Show first
      </div>
      <Choice
        checked={first === NEWEST}
        onClick={() => pick(NEWEST)}
        label="Newest orders"
        hint="As the list always was"
      />
      <Choice
        checked={first === ALL_CARRIERS}
        onClick={() => pick(ALL_CARRIERS)}
        label="Every carrier's orders"
        hint="Then self delivery and pickup"
      />
      <div className="mx-[10px] my-[4px] border-t-[1px] border-ui-border" />
      {carriers.map((carrier) => (
        <Choice
          key={carrier.value}
          checked={first === carrier.value}
          onClick={() => pick(carrier.value)}
          label={`${carrierDisplayName(carrier.label)} first`}
        />
      ))}
      <p className="m-0 px-[10px] pb-[4px] pt-[6px] text-[11px] text-ui-text-subtle">
        Then the other carriers, then the rest, newest first. Kept on this
        device.
      </p>
    </div>
  );

  return (
    <div ref={groupRef} className="flex items-stretch">
      <button
        type="button"
        aria-pressed={on}
        onClick={() => onChange(!on)}
        title={
          on
            ? "Back to all orders"
            : "Show the orders to fulfil that leave with a carrier"
        }
        className={`${segment(on)} rounded-l-md`}
      >
        <MdLocalShipping className="h-3 w-3 shrink-0" />
        Carriers
        {on && (
          <IoClose aria-hidden="true" className="-mr-[3px] h-3 w-3 shrink-0" />
        )}
      </button>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={shownFirst ? `Show first: ${shownFirst}` : "Show first"}
        title="Which orders the list shows first"
        onClick={() => setOpen((value) => !value)}
        className={`${segment(Boolean(first))} -ml-px rounded-r-md px-[6px]`}
      >
        {shownFirst && <span className="pl-[4px]">{shownFirst}</span>}
        <MdArrowDropDown aria-hidden="true" className="h-4 w-4 shrink-0" />
      </button>
      {panel && createPortal(panel, document.body)}
    </div>
  );
};

export default CarrierOrdersMenu;
